import React from "react";

/**
 * Custom hook for debounced localStorage writes
 * Batches rapid state updates and writes to localStorage after a delay
 * This prevents blocking the UI thread on every state change
 */
export function useDebouncedLocalStorage(key, initialValue, delay = 300) {
    const [storedValue, setStoredValue] = React.useState(() => {
        if (typeof window === "undefined") {
            return initialValue;
        }

        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error(error);
            return initialValue;
        }
    });

    const timeoutRef = React.useRef(null);
    const pendingValueRef = React.useRef(undefined);

    const persist = React.useCallback((value) => {
        if (typeof window === "undefined") return;
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(error);
        }
    }, [key]);

    const setDebouncedValue = React.useCallback(
        (value) => {
            setStoredValue((currentValue) => {
                const valueToStore = value instanceof Function ? value(currentValue) : value;
                pendingValueRef.current = valueToStore;
                return valueToStore;
            });

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                if (pendingValueRef.current !== undefined) {
                    persist(pendingValueRef.current);
                    pendingValueRef.current = undefined;
                }
            }, delay);
        },
        [delay, persist]
    );

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (pendingValueRef.current !== undefined) {
                persist(pendingValueRef.current);
                pendingValueRef.current = undefined;
            }
        };
    }, [persist]);

    return [storedValue, setDebouncedValue];
}
